import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth/authStore';
import { toast } from '@/components/ui/Toast';
import { authApi } from './api';
import { usePlanContext } from '@onxite/plan-sdk';

/**
 * Landing page hit after backend redirects from `/auth/<provider>/callback`.
 * Reads `?code=<one-time-token>&isNewUser=0|1`, swaps the code for a real JWT
 * via POST /auth/oauth-exchange, sets the session, surfaces the plan alert
 * (if any) into the SDK context, and routes the user onward:
 *
 *   isNewUser → /onboarding/brand-profile  (company doesn't exist yet)
 *   otherwise → /                          (dashboard)
 *
 * Failures (bad/expired code) toast and bounce back to /login.
 */
export function OAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { reportPlanAlert } = usePlanContext();
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const code = params.get('code');
    const isNewUser = params.get('isNewUser') === '1';
    if (!code) {
      toast({ tone: 'error', title: 'Missing OAuth code' });
      navigate('/login', { replace: true });
      return;
    }

    void (async () => {
      try {
        const res = await authApi.oauthExchange(code);
        setSession({
          token: res.access_token,
          refreshToken: null,
          user: res.user,
          role:
            typeof res.user?.role === 'string'
              ? (res.user.role as any)
              : (res.user?.role?.name as any) ?? undefined,
        });
        reportPlanAlert(res.planAlert ?? null);
        if (isNewUser) {
          navigate('/onboarding/brand-profile', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err: any) {
        toast({
          tone: 'error',
          title: 'OAuth exchange failed',
          description: err?.message ?? 'Please try signing in again.',
        });
        navigate('/login', { replace: true });
      }
    })();
  }, [params, navigate, setSession, reportPlanAlert]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-sm text-[var(--color-fg-muted)]">Signing you in…</div>
    </div>
  );
}
